import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import Typography from '@material-ui/core/Typography';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import DisabledPage from './logged/DisabledPage';
import { setData } from '../actions/index';
import { getPupilMenuItems } from './PupilMenuItems';
import ErrorBoundary from './parts/Error/ErrorBoundary';

function TabContainer({ children }) {
  return (
    <Typography component="div" style={{ padding: 24 }}>
      {children}
    </Typography>
  );
}

const styles = theme => ({
  appBar: {
    color: '#000',
    backgroundColor: '#fff',
    boxShadow: '0 4px 4px -2px gray',
  },
  content: {
    padding: 32,
  },
  tabsIndicator: {
    backgroundColor: '#fff',
  },
});

const mapStateToProps = state => {
  return {
    messagesSelectedThread: state.messagesSelectedThread,
    selectedPupil: state.selectedPupil,
    openModal: state.openModal,
    user: state.user,
  };
};

function mapDispatchToProps(dispatch) {
  return {
    setData: (data, type) => dispatch(setData(data, type)),
  };
}

class MobilePupilPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      tabIndex: 0,
      tabs: {},
    };

    this.onAppViewChange = this.onAppViewChange.bind(this);
    this.onChange = this.onChange.bind(this);
    this.prepareTabs = this.prepareTabs.bind(this);
  }

  componentDidMount() {
    this.prepareTabs();

    window.addEventListener('app:change-view', this.onAppViewChange);
  }

  componentWillUnmount() {
    window.removeEventListener('app:change-view', this.onAppViewChange);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.selectedPupil !== this.props.selectedPupil) {
      this.prepareTabs();
    }
  }

  onAppViewChange(event) {
    const type = event.detail && event.detail.type
      ? event.detail.type
      : null;
    const teacher_id = event.detail && event.detail.teacher_id
      ? event.detail.teacher_id
      : null;

    if (type === 'messages' && teacher_id) {
      clearTimeout(this.threadsDefaultSearchTimeout);

      this.threadsDefaultSearch = String(event.detail.teacher_id);

      this.threadsDefaultSearchTimeout = setTimeout(() => {
        // This is a hacky workaround for the issue, that basically always opens
        // the exact same teacher thread as the first time, if you visit the messaging page.
        // With that workaround we unset the search (teacher_id) after 8 seconds,
        // so that should give us enough time to render the new tabs + containers,
        // load the threads list & also select the one thread we set here.
        this.threadsDefaultSearch = '';
      }, 8000);
    }

    // For some strange reason the componentDidUpdate does not fire
    // when the selected pupil gets changed as a part of the /pushnotification endpoint,
    // so let's just manually trigger & redraw the tabs
    this.prepareTabs();
  }

  prepareTabs() {
    const {
      t,
      selectedPupil,
      openModal,
      user,
    } = this.props;
    if (selectedPupil === null) {
      return;
    }

    const defaultView = selectedPupil.defaultView
      ? selectedPupil.defaultView
      : 'behaviour';

    const threadsDefaultSearch = this.threadsDefaultSearch
      ? this.threadsDefaultSearch
      : '';

    const tabs = getPupilMenuItems(selectedPupil, user, threadsDefaultSearch);
    tabs.disabled = {
      key: 'disabled',
      label: t('Disabled'),
      component: <DisabledPage useSuspense={false} />,
      is_in_menu: false,
    };

    let tabIndex = 0;

    if (Object.keys(tabs)[0] !== defaultView) {
      for (let i = 0; i < Object.keys(tabs).length; i++) {
        const tab = tabs[Object.keys(tabs)[i]];
        if (tab.key === defaultView) {
          tabIndex = i;
          break;
        }
      }
    }

    let hash = `${selectedPupil.id},${defaultView}`;
    if (openModal) {
      hash += ',' + openModal;
    }
    window.location.hash = hash;

    this.setState({
      tabs,
      tabIndex,
    });
  }

  onChange(event, tabIndex) {
    const {
      selectedPupil,
      openModal,
    } = this.props;

    this.setState({
      tabIndex,
    });

    if (selectedPupil === null) {
      return;
    }

    const view = Object.keys(this.state.tabs)[tabIndex];
    let hash = `${selectedPupil.id},${view}`;
    if (openModal) {
      hash += ',' + openModal;
    }

    window.location.hash = hash;
  }

  render() {
    const {
      tabIndex,
      tabs,
    } = this.state;
    const {
      classes,
      selectedPupil,
    } = this.props;

    const hasTabs = Object.keys(tabs).length > 0
      && !(Object.keys(tabs).length === 1
      && typeof tabs.disabled !== 'undefined');

    // TODO: implement swipable views, once I figure out the height issue

    return (
      <ErrorBoundary location="MobilePupilPage.js">
      <div key={selectedPupil ? selectedPupil.id : 0}>
        {hasTabs && (
          <AppBar position="static" className={classes.appBar}>
            <Tabs
              value={tabIndex}
              onChange={this.onChange}
              variant="scrollable"
              classes={{ indicator: classes.tabsIndicator }}
            >
              {Object.keys(tabs).map((key) => {
                if (tabs[key]?.is_in_menu === false) {
                  return '';
                }

                return <Tab key={key} label={tabs[key].label} />;
              })}
            </Tabs>
          </AppBar>
        )}
        {Object.keys(tabs).map((key, index) => {
          if (tabIndex !== index) {
            return;
          }

          if (tabs[key].mobileWithoutContainer) {
            return (
              <ErrorBoundary key={key} location={`MobilePupilPage.js, tab:${key}`}>
                {tabs[key].component}
              </ErrorBoundary>
            );
          }

          return (
            <ErrorBoundary key={key} location={`MobilePupilPage.js, tab:${key}`}>
              <TabContainer>{tabs[key].component}</TabContainer>
            </ErrorBoundary>
          );
        })}
      </div>
      </ErrorBoundary>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(withTranslation()(MobilePupilPage))
);
